import {FC, useEffect, useState} from "react";
import {Alert, App, Button, Card, Col, Empty, Form, Input, Row, Select, Space, Switch, Tag, Tooltip, Typography} from "antd";
import {EyeInvisibleOutlined, LockOutlined, MailOutlined, QuestionCircleOutlined, SaveOutlined, ThunderboltOutlined, UserOutlined} from "@ant-design/icons";
import AxiosServices from "../../Services/axiosServices";
import {useAuth} from "../../config/AuthContext";

const ACCENT = '#203923';
const {Paragraph, Text} = Typography;

const TITLE_OPTIONS = [
    {value: '1', label: 'Monsieur'},
    {value: '2', label: 'Madame'},
    {value: '3', label: 'Mademoiselle'},
];

type ProfileData = {
    idt: number;
    username?: string;
    email?: string;
    role?: string;
    firstName?: string;
    lastName?: string;
    verified?: boolean;
    hideName?: boolean;
    hideEmail?: boolean;
    randomBet?: boolean;
    Title?: {code: string; libelle?: string};
};

const Profile: FC = () => {
    const {isLoggedIn, setAuthUser} = useAuth();
    const {message} = App.useApp();
    const [loading, setLoading] = useState(false);
    const [savingProfile, setSavingProfile] = useState(false);
    const [savingPassword, setSavingPassword] = useState(false);
    const [profile, setProfile] = useState<ProfileData | null>(null);
    const [profileForm] = Form.useForm();
    const [passwordForm] = Form.useForm();

    useEffect(() => {
        if (!isLoggedIn) return;
        setLoading(true);
        AxiosServices.getMyProfile()
            .then((data: ProfileData) => {
                setProfile(data);
                profileForm.setFieldsValue({
                    username: data.username,
                    Title: data.Title?.code,
                    firstName: data.firstName,
                    lastName: data.lastName,
                    hideName: data.hideName ?? true,
                    hideEmail: data.hideEmail ?? true,
                    randomBet: data.randomBet ?? false,
                });
            })
            .catch((err) => {
                const msg = err?.response?.data?.error ?? err?.message ?? 'Erreur inconnue';
                message.error(`Impossible de charger le profil : ${msg}`);
            })
            .finally(() => setLoading(false));
    }, [isLoggedIn, profileForm, message]);

    if (!isLoggedIn) {
        return (
            <div style={{padding: 24}}>
                <Empty description="Connecte-toi pour accéder à ton profil."/>
            </div>
        );
    }

    const onProfileSubmit = async (values: {Title?: string; firstName?: string; lastName?: string; username?: string; hideName?: boolean; hideEmail?: boolean; randomBet?: boolean}) => {
        setSavingProfile(true);
        try {
            const updated: ProfileData = await AxiosServices.updateMyProfile(values);
            setProfile(updated);
            setAuthUser(updated as any);
            message.success('Profil mis à jour.');
        } catch (err: any) {
            const msg = err?.response?.data?.error ?? err?.message ?? 'Erreur inconnue';
            message.error(`Échec de la mise à jour : ${msg}`);
        } finally {
            setSavingProfile(false);
        }
    };

    const onPasswordSubmit = async (values: {currentPassword: string; newPassword: string}) => {
        setSavingPassword(true);
        try {
            await AxiosServices.changePassword(values.currentPassword, values.newPassword);
            passwordForm.resetFields();
            message.success('Mot de passe modifié.');
        } catch (err: any) {
            const msg = err?.response?.data?.error ?? err?.message ?? 'Erreur inconnue';
            message.error(`Échec : ${msg}`);
        } finally {
            setSavingPassword(false);
        }
    };

    return (
        <div style={{padding: 16}}>
            <Row gutter={[16, 16]}>
                <Col xs={24} lg={14}>
                    <Card
                        title={<Space><UserOutlined/><span>Mon profil</span></Space>}
                        loading={loading}
                        styles={{header: {background: ACCENT, color: '#fff'}}}
                    >
                        {profile && (
                            <>
                                <Paragraph>
                                    <Text strong><MailOutlined/> Email : </Text>
                                    <Text>{profile.email}</Text>
                                    {profile.role && (
                                        <Tag style={{marginLeft: 8}} color={profile.role.toUpperCase() === 'ADMIN' ? 'gold' : 'blue'}>
                                            {profile.role}
                                        </Tag>
                                    )}
                                    {profile.verified === false && (
                                        <Tag color="red" style={{marginLeft: 8}}>Non vérifié</Tag>
                                    )}
                                </Paragraph>
                                <Form
                                    form={profileForm}
                                    layout="vertical"
                                    onFinish={onProfileSubmit}
                                    requiredMark={false}
                                >
                                    <Form.Item
                                        name="username"
                                        label="Identifiant"
                                        rules={[
                                            {required: true, message: 'Identifiant requis'},
                                            {min: 2, message: '2 caractères minimum'},
                                        ]}
                                    >
                                        <Input/>
                                    </Form.Item>
                                    <Row gutter={12}>
                                        <Col xs={24} sm={6}>
                                            <Form.Item
                                                name="Title"
                                                label="Civilité"
                                                rules={[{required: true, message: 'Civilité requise'}]}
                                            >
                                                <Select options={TITLE_OPTIONS} placeholder="Choisir…"/>
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={9}>
                                            <Form.Item
                                                name="firstName"
                                                label="Prénom"
                                                rules={[{required: true, message: 'Prénom requis'}]}
                                            >
                                                <Input/>
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={9}>
                                            <Form.Item
                                                name="lastName"
                                                label="Nom"
                                                rules={[{required: true, message: 'Nom requis'}]}
                                            >
                                                <Input/>
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Form.Item style={{marginBottom: 12}}>
                                        <Space size={8} align="center">
                                            <Form.Item name="hideName" valuePropName="checked" noStyle>
                                                <Switch/>
                                            </Form.Item>
                                            <Text>
                                                <EyeInvisibleOutlined/> Masquer mon nom dans les listes publiques
                                            </Text>
                                            <Tooltip title="Quand activé, ton prénom et nom n'apparaissent ni dans l'annuaire ni dans les vues consultées par les autres joueurs. Ton identifiant et tes scores restent visibles.">
                                                <QuestionCircleOutlined style={{color: 'rgba(0,0,0,0.45)'}}/>
                                            </Tooltip>
                                        </Space>
                                    </Form.Item>
                                    <Form.Item style={{marginBottom: 12}}>
                                        <Space size={8} align="center">
                                            <Form.Item name="hideEmail" valuePropName="checked" noStyle>
                                                <Switch/>
                                            </Form.Item>
                                            <Text>
                                                <EyeInvisibleOutlined/> Masquer mon adresse email dans les listes publiques
                                            </Text>
                                            <Tooltip title="Quand activé, ton adresse email n'apparaît ni dans l'annuaire ni dans les vues consultées par les autres joueurs.">
                                                <QuestionCircleOutlined style={{color: 'rgba(0,0,0,0.45)'}}/>
                                            </Tooltip>
                                        </Space>
                                    </Form.Item>
                                    <Form.Item style={{marginBottom: 24}}>
                                        <Space size={8} align="center">
                                            <Form.Item name="randomBet" valuePropName="checked" noStyle>
                                                <Switch/>
                                            </Form.Item>
                                            <Text>
                                                <ThunderboltOutlined/> Vote aléatoire pour les matchs non pariés
                                            </Text>
                                            <Tooltip title="Quand activé, les matchs auxquels tu n'as pas pronostiqué reçoivent automatiquement un score tiré au hasard plutôt que d'être laissés vides.">
                                                <QuestionCircleOutlined style={{color: 'rgba(0,0,0,0.45)'}}/>
                                            </Tooltip>
                                        </Space>
                                    </Form.Item>
                                    <Form.Item>
                                        <Button
                                            type="primary"
                                            icon={<SaveOutlined/>}
                                            htmlType="submit"
                                            loading={savingProfile}
                                        >
                                            Enregistrer
                                        </Button>
                                    </Form.Item>
                                </Form>
                            </>
                        )}
                    </Card>
                </Col>

                <Col xs={24} lg={10}>
                    <Card
                        title={<Space><LockOutlined/><span>Mot de passe</span></Space>}
                        styles={{header: {background: ACCENT, color: '#fff'}}}
                    >
                        <Alert
                            type="info"
                            showIcon
                            message="Saisis ton mot de passe actuel puis le nouveau."
                            style={{marginBottom: 12}}
                        />
                        <Form
                            form={passwordForm}
                            layout="vertical"
                            onFinish={onPasswordSubmit}
                            requiredMark={false}
                        >
                            <Form.Item
                                name="currentPassword"
                                label="Mot de passe actuel"
                                rules={[{required: true, message: 'Requis'}]}
                            >
                                <Input.Password autoComplete="current-password"/>
                            </Form.Item>
                            <Form.Item
                                name="newPassword"
                                label="Nouveau mot de passe"
                                rules={[
                                    {required: true, message: 'Requis'},
                                    {min: 6, message: '6 caractères minimum'},
                                ]}
                            >
                                <Input.Password autoComplete="new-password"/>
                            </Form.Item>
                            <Form.Item
                                name="confirm"
                                label="Confirmation"
                                dependencies={['newPassword']}
                                rules={[
                                    {required: true, message: 'Requis'},
                                    ({getFieldValue}) => ({
                                        validator(_, value) {
                                            if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                                            return Promise.reject(new Error('Les deux mots de passe doivent être identiques.'));
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password autoComplete="new-password"/>
                            </Form.Item>
                            <Form.Item>
                                <Button
                                    type="primary"
                                    icon={<LockOutlined/>}
                                    htmlType="submit"
                                    loading={savingPassword}
                                    danger
                                >
                                    Modifier le mot de passe
                                </Button>
                            </Form.Item>
                        </Form>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default Profile;
